import { useCallback, useEffect, useState } from 'react'

/**
 * Custom hook for handling text selection in bot messages
 * @param {HTMLElement} containerElement - The container element to listen for text selection
 * @param {Object} options - Configuration options
 * @param {string[]} options.excludeSelectors - CSS selectors to exclude from text selection
 * @param {boolean} options.isPopupEnabled - Whether the popup should be enabled (similar to isButtonActive)
 * @returns {Object} - Text selection state and handlers
 */
const useTextSelection = (containerElement, options = {}) => {
    const {
        excludeSelectors = [
            '.user-message',
            '.typing-indicator',
            '.page-reference',
            '.page-reference-tooltip',
            '.reference-link',
            '.page-number',
        ],
        isPopupEnabled = true, // Default to true for backward compatibility
    } = options

    const [selectedText, setSelectedText] = useState('')
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 })
    const [showPopup, setShowPopup] = useState(false)

    // Function to clean selected text by removing reference link numbers
    const cleanSelectedText = useCallback((selection) => {
        if (!selection.rangeCount) {
            return selection.toString().trim()
        }

        const range = selection.getRangeAt(0)
        const clonedRange = range.cloneRange()

        // Get all reference-link elements within the selection
        const container =
            range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                ? range.commonAncestorContainer.parentElement
                : range.commonAncestorContainer

        const referenceLinkElements = container.querySelectorAll(
            '.reference-link, .page-number'
        )

        // Create a document fragment to work with
        const fragment = clonedRange.cloneContents()

        // Remove reference link elements from the fragment
        const fragmentReferenceLinkElements = fragment.querySelectorAll(
            '.reference-link, .page-number'
        )
        fragmentReferenceLinkElements.forEach((element) => {
            element.remove()
        })

        // Get the cleaned text
        const cleanedText = fragment.textContent || ''

        return cleanedText.trim()
    }, [])

    const isBotMessageSelection = useCallback(
        (selection) => {
            if (!selection.rangeCount) {
                return false
            }

            const range = selection.getRangeAt(0)
            const selectedElement =
                range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                    ? range.commonAncestorContainer.parentElement
                    : range.commonAncestorContainer

            // Check if selection is in any excluded elements
            for (const selector of excludeSelectors) {
                if (selectedElement.closest(selector)) {
                    return false
                }
            }

            // Check if selection is in bot message
            const botMessage = selectedElement.closest('.bot-message')
            if (!botMessage) {
                return false
            }

            // Check if selection is in text bubble
            const textBubble = selectedElement.closest('.text-bubble')
            if (!textBubble) {
                return false
            }

            // Check if this is a greeting message by looking for the greeting text pattern
            const selectedText = selection.toString().trim()
            const textContent = textBubble.textContent || ''

            // If the text bubble contains greeting text, don't show popup
            if (
                textContent.includes(
                    "Hello! I'm an AI assistant specialized in"
                )
            ) {
                return false
            }

            return true
        },
        [excludeSelectors]
    )

    const handleMouseUp = useCallback(
        (event) => {
            // Check if popup is enabled (similar to isButtonActive check)
            if (!isPopupEnabled) {
                setShowPopup(false)
                return
            }

            // Get selection immediately
            const selection = window.getSelection()
            const rawText = selection.toString().trim()

            if (!rawText || rawText.length === 0) {
                setShowPopup(false)
                return
            }

            const cleanedText = cleanSelectedText(selection)

            if (!cleanedText || cleanedText.length === 0) {
                setShowPopup(false)
                return
            }

            if (!selection.rangeCount) {
                setShowPopup(false)
                return
            }

            if (!containerElement) {
                setShowPopup(false)
                return
            }

            const range = selection.getRangeAt(0)
            const isWithinContainer = containerElement.contains(
                range.commonAncestorContainer
            )

            if (!isWithinContainer) {
                setShowPopup(false)
                return
            }

            const isValidSelection = isBotMessageSelection(selection)
            if (!isValidSelection) {
                setShowPopup(false)
                return
            }

            // Get individual rectangles for each line of the selection
            const rects = range.getClientRects()

            if (rects.length === 0) {
                // Fallback to overall bounding box
                const rect = range.getBoundingClientRect()
                var x = rect.right - 72
                var y = rect.top - 42
            } else {
                // Find the rightmost position on the first line
                const firstRect = rects[0]
                const firstLineTop = firstRect.top
                const firstLineBottom = firstRect.bottom

                // Find all rectangles that belong to the first line
                let rightmostX = firstRect.right
                let firstLineY = firstRect.top

                for (let i = 0; i < rects.length; i++) {
                    const rect = rects[i]
                    // Check if this rectangle is on the same line (within tolerance)
                    if (
                        Math.abs(rect.top - firstLineTop) < 5 &&
                        Math.abs(rect.bottom - firstLineBottom) < 5
                    ) {
                        // This rectangle is on the first line, update rightmost position
                        rightmostX = Math.max(rightmostX, rect.right)
                    } else {
                        // This rectangle is on a different line, stop checking
                        break
                    }
                }

                // Calculate popup position using the rightmost position on first line
                var x = rightmostX - 72 // Align popup with the actual end of first line
                var y = firstLineY - 42 // Position above first line
            }

            // Boundary check to ensure popup doesn't go outside viewport
            const popupWidth = 72
            const popupHeight = 36
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight

            // Desktop positioning
            if (x < 10) {
                x = 10
            } else if (x + popupWidth > viewportWidth - 10) {
                x = viewportWidth - popupWidth - 10
            }

            if (y < 10) {
                // If not enough space above, show below the first line
                const fallbackY =
                    rects.length > 0
                        ? rects[0].bottom + 10
                        : range.getBoundingClientRect().bottom + 10
                y = fallbackY
            }

            setSelectedText(cleanedText)
            setPopupPosition({ x, y })
            setShowPopup(true)
        },
        [
            containerElement,
            isBotMessageSelection,
            cleanSelectedText,
            isPopupEnabled,
        ]
    )

    const handleSelectionChange = useCallback(() => {
        const selection = window.getSelection()
        const text = selection.toString().trim()

        // If no text is selected and popup is showing, hide it
        if (!text && showPopup) {
            setShowPopup(false)
            setSelectedText('')
            return
        }

        // If text is selected but popup is not showing, don't interfere
        if (text && !showPopup) {
            return
        }

        // If selected text is not from bot message, hide popup (but don't prevent text selection)
        if (text && showPopup && !isBotMessageSelection(selection)) {
            setShowPopup(false)
        }
    }, [isBotMessageSelection, showPopup])

    const closePopup = useCallback((clearSelection = true) => {
        if (clearSelection) {
            // First hide the popup
            setShowPopup(false)
            // Then clear the text state
            setSelectedText('')
            // Finally clear the browser selection
            if (window.getSelection) {
                window.getSelection().removeAllRanges()
            }
        } else {
            // Only hide the popup, keep everything else
            setShowPopup(false)
        }
    }, [])

    // Handle scroll events to clear selection and hide popup
    const handleScroll = useCallback(() => {
        if (showPopup) {
            // Clear selection and hide popup when scrolling
            setShowPopup(false)
            setSelectedText('')
            if (window.getSelection) {
                window.getSelection().removeAllRanges()
            }
        }
    }, [showPopup])

    useEffect(() => {
        if (!containerElement) {
            return
        }

        containerElement.addEventListener('mouseup', handleMouseUp)
        containerElement.addEventListener('scroll', handleScroll)
        document.addEventListener('selectionchange', handleSelectionChange)

        return () => {
            containerElement.removeEventListener('mouseup', handleMouseUp)
            containerElement.removeEventListener('scroll', handleScroll)
            document.removeEventListener(
                'selectionchange',
                handleSelectionChange
            )
        }
    }, [containerElement, handleMouseUp, handleSelectionChange, handleScroll])

    return {
        selectedText,
        popupPosition,
        showPopup,
        closePopup,
    }
}

export default useTextSelection
